import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Chip, Card, CardContent, CardActions, Grid, Button,
  TextField, MenuItem, Stack, IconButton, Divider, Tooltip, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon, MeetingRoom as RoomIcon,
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  ViewList as TableListIcon, GridView as GridViewIcon, ArrowBack as ArrowBackIcon,
  DateRange as DateRangeIcon, EventRepeat as RepeatIcon, CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import GenericTable, { ColumnConfig } from '../../Commun/components/GenericTable';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { useAuth } from '../../Commun/contexts/AuthContext';
import { academyStore } from '../services/academyStore';
import { planningTheoriqueApi } from '../services/planningTheoriqueApi';
import { PlanningTheorique } from '../types/planningTheorique';

const ENTITY_KEY = 'Blobtic_Academy,academy_planning_theorique';
const PRIMARY_KEY = 'THEO_IDF';

const emptyPlanning = (dosId: number = 1): Partial<PlanningTheorique> => {
  const today = new Date().toISOString().split('T')[0];
  return {
    dos_idf: dosId,
    arm_idf: 0,
    nbr_week: 1,
    start_date: today,
    end_date: null,
    day_type: 'Normal'
  };
};

const planningToDto = (p: Partial<PlanningTheorique>, dosId: number): Record<string, any> => {
  return {
    THEO_IDF: (p as any).THEO_IDF ?? (p.id && p.id > 0 ? p.id : undefined),
    DOS_IDF: p.dos_idf || dosId,
    ARM_IDF: p.arm_idf || 0,
    THEO_NBR_WEEK: p.nbr_week || 1,
    THEO_START_DATE: p.start_date || '',
    THEO_END_DATE: p.end_date || null,
    THEO_DAY_TYPE: p.day_type || 'Normal'
  };
};

export const TheoreticalPlanningManagePage: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [, setTick] = useState(0);

  const rawAgency = user?.agency;
  const parsedDosId = rawAgency ? parseInt(String(rawAgency), 10) : 1;
  const connectedDosId = (!isNaN(parsedDosId) && parsedDosId > 0) ? parsedDosId : 1;

  // View state: 'list' (default) or 'form' (new/edit)
  const [view, setView] = useState<'list' | 'form'>('list');
  const [viewMode, setViewMode] = useDefaultViewMode();

  const [loading, setLoading] = useState(false);
  const [plannings, setPlannings] = useState<PlanningTheorique[]>([]);
  const [formData, setFormData] = useState<Partial<PlanningTheorique>>(() => emptyPlanning(connectedDosId));
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    academyStore.ensureRoomsLoaded();
    return academyStore.subscribe(() => setTick(tick => tick + 1));
  }, []);

  const rooms = academyStore.getRooms();

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await planningTheoriqueApi.getPlanningTheoriques();
      setPlannings(data);
    } catch (err: any) {
      blobticMessage.error(t('commun.error'), err?.message || 'Erreur lors du chargement des plannings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // ── Navigation & Actions ──

  const openNew = () => {
    const defaultRoomId = rooms.length > 0 ? rooms[0].id : 0;
    const initial = emptyPlanning(connectedDosId);
    initial.arm_idf = defaultRoomId;
    setFormData(initial);
    setIsEditing(false);
    setView('form');
  };

  const openEdit = (item: PlanningTheorique) => {
    setFormData({ ...item });
    setIsEditing(true);
    setView('form');
  };

  const backToList = () => {
    setView('list');
    setFormData(emptyPlanning(connectedDosId));
    setIsEditing(false);
  };

  const handleValidate = (): string | null => {
    if (!formData.arm_idf || formData.arm_idf <= 0) {
      return t('academy.planning_theorique.validation.room_required');
    }
    if (!formData.nbr_week || formData.nbr_week <= 0) {
      return t('academy.planning_theorique.validation.weeks_required');
    }
    if (!formData.start_date) {
      return t('academy.planning_theorique.validation.start_date_required');
    }
    if (formData.end_date && formData.start_date && formData.end_date < formData.start_date) {
      return t('academy.planning_theorique.validation.invalid_date_range');
    }
    return null;
  };

  const handleDataChange = async () => {
    await loadData();
    backToList();
  };

  const handleClear = () => backToList();

  const handleDelete = async (item: PlanningTheorique) => {
    const confirmed = await blobticMessage.confirm({
      title: t('commun.confirmation'),
      message: t('academy.planning_theorique.confirmDelete') || 'Êtes-vous sûr de vouloir supprimer ce planning théorique ? Les créneaux associés seront également supprimés.',
      severity: 'warning'
    });
    if (!confirmed) return;

    try {
      const res = await planningTheoriqueApi.deletePlanningTheorique(item.id);
      if (res && res.success === false) {
        blobticMessage.error(t('commun.error'), res.message || res.error || 'Erreur de suppression.');
        return;
      }
      blobticMessage.success(t('commun.success'), t('academy.planning_theorique.msg_deleted'));
      await loadData();
    } catch (error: any) {
      blobticMessage.error(t('commun.error'), error?.message || 'Erreur lors de la suppression.');
    }
  };

  // ── Configuration du GenericTable ──

  const tableColumns: ColumnConfig[] = [
    {
      id: 'arm_idf',
      label: t('academy.planning_theorique.room'),
      render: (row: PlanningTheorique) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <RoomIcon fontSize="small" color="primary" />
          <Typography variant="body2" fontWeight={600}>
            {row.room_number ? `${row.room_number} ${row.room_lib ? `(${row.room_lib})` : ''}` : `Salle #${row.arm_idf}`}
          </Typography>
        </Stack>
      )
    },
    {
      id: 'nbr_week',
      label: t('academy.planning_theorique.nbr_weeks'),
      render: (row: PlanningTheorique) => (
        <Chip
          icon={<RepeatIcon fontSize="small" />}
          label={`${row.nbr_week} ${row.nbr_week > 1 ? t('academy.planning_theorique.weeks') : t('academy.planning_theorique.week')}`}
          size="small"
          color="secondary"
          variant="outlined"
        />
      )
    },
    {
      id: 'start_date',
      label: t('academy.planning_theorique.start_date'),
      render: (row: PlanningTheorique) => (
        <Typography variant="body2">{row.start_date}</Typography>
      )
    },
    {
      id: 'end_date',
      label: t('academy.planning_theorique.end_date'),
      render: (row: PlanningTheorique) => (
        <Typography variant="body2">{row.end_date || '—'}</Typography>
      )
    },
    {
      id: 'day_type',
      label: t('academy.planning_theorique.day_type'),
      render: (row: PlanningTheorique) => {
        const isHoliday = row.day_type === 'Holiday';
        return (
          <Chip
            label={isHoliday ? t('academy.planning_theorique.type_holiday') : t('academy.planning_theorique.type_normal')}
            size="small"
            color={isHoliday ? 'warning' : 'success'}
          />
        );
      }
    },
    {
      id: 'vacs_count',
      label: t('academy.planning_theorique.vacs_count'),
      render: (row: PlanningTheorique) => (
        <Chip label={`${row.vacs_count || 0} créneau(x)`} size="small" variant="filled" />
      )
    }
  ];

  // ── Render: Form View ──

  if (view === 'form') {
    const isEditing = !!(formData as any).THEO_IDF || !!(formData.id && formData.id > 0);
    const formPayload = planningToDto(formData, connectedDosId);

    return (
      <GenericPage
        title={
          isEditing
            ? t('academy.planning_theorique.edit_title')
            : t('academy.planning_theorique.new_title')
        }
        icon={<CalendarIcon />}
        footerEnabled={true}
        footerInfo={{
          lastUpdate: formData.last_updt ? new Date(formData.last_updt).toLocaleString('fr-DZ') : '-',
          userLib: '-'
        }}
        additionalActions={
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={backToList}
            sx={{ borderRadius: 2 }}
          >
            {t('common.back_to_list') || t('commun.back_to_list')}
          </Button>
        }
        crudConfig={{
          entityKey: ENTITY_KEY,
          primaryKey: PRIMARY_KEY
        }}
        data={formPayload}
        onDataChange={handleDataChange}
        onValidate={handleValidate}
        onClear={handleClear}
      >
        <Box sx={{ maxWidth: 800, mx: 'auto', pb: 4 }}>
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon color="primary" />
              {isEditing ? t('academy.planning_theorique.edit_title') : t('academy.planning_theorique.new_title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('academy.planning_theorique.form_subtitle')}
            </Typography>

            <Grid container spacing={2.5}>
              {/* 1. Sélection Salle */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  required
                  label={t('academy.planning_theorique.room')}
                  value={formData.arm_idf || ''}
                  onChange={(e) => setFormData({ ...formData, arm_idf: Number(e.target.value) })}
                  helperText={t('academy.planning_theorique.room_helper')}
                >
                  {rooms.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.room_number} {r.floor ? `(${r.floor})` : ''}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* 2. Nombre de semaines */}
              <Grid item xs={12} sm={6}>
                <TextField
                  type="number"
                  fullWidth
                  required
                  inputProps={{ min: 1, max: 52 }}
                  label={t('academy.planning_theorique.nbr_weeks')}
                  value={formData.nbr_week || 1}
                  onChange={(e) => setFormData({ ...formData, nbr_week: Math.max(1, Number(e.target.value)) })}
                  helperText={t('academy.planning_theorique.weeks_helper')}
                />
              </Grid>

              {/* 3. Date Début */}
              <Grid item xs={12} sm={6}>
                <TextField
                  type="date"
                  fullWidth
                  required
                  label={t('academy.planning_theorique.start_date')}
                  value={formData.start_date || ''}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* 4. Date Fin */}
              <Grid item xs={12} sm={6}>
                <TextField
                  type="date"
                  fullWidth
                  label={t('academy.planning_theorique.end_date')}
                  value={formData.end_date || ''}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value || null })}
                  InputLabelProps={{ shrink: true }}
                  helperText={t('academy.planning_theorique.end_date_helper')}
                />
              </Grid>

              {/* 5. Type de Jour */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label={t('academy.planning_theorique.day_type')}
                  value={formData.day_type || 'Normal'}
                  onChange={(e) => setFormData({ ...formData, day_type: e.target.value as 'Normal' | 'Holiday' })}
                >
                  <MenuItem value="Normal">{t('academy.planning_theorique.type_normal')}</MenuItem>
                  <MenuItem value="Holiday">{t('academy.planning_theorique.type_holiday')}</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </GenericPage>
    );
  }

  // ── Render: List View (Grid vs Table) ──

  return (
    <GenericPage
      title={t('academy.planning_theorique.manage_title')}
      icon={<CalendarIcon />}
      footerEnabled={false}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      onNew={openNew}
      newButtonLabel={t('academy.planning_theorique.new_button')}
    >
      <Box sx={{ pb: 4 }}>
        {viewMode === 'grid' ? (
          <Grid container spacing={2.5}>
            {plannings.map((item) => {
              const isHoliday = item.day_type === 'Holiday';
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                  <Card
                    elevation={2}
                    sx={{
                      borderRadius: 3,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: 4
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <RoomIcon color="primary" />
                          <Typography variant="h6" fontWeight={700}>
                            {item.room_number ? `${item.room_number}` : `Salle #${item.arm_idf}`}
                          </Typography>
                        </Stack>
                        <Chip
                          label={isHoliday ? t('academy.planning_theorique.type_holiday') : t('academy.planning_theorique.type_normal')}
                          size="small"
                          color={isHoliday ? 'warning' : 'success'}
                          variant="filled"
                        />
                      </Stack>

                      {item.room_lib && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {item.room_lib}
                        </Typography>
                      )}

                      <Stack spacing={1} sx={{ mt: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <RepeatIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            <strong>{item.nbr_week}</strong> {item.nbr_week > 1 ? t('academy.planning_theorique.weeks') : t('academy.planning_theorique.week')}
                          </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center">
                          <DateRangeIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {item.start_date} → {item.end_date || t('academy.planning_theorique.indefinite')}
                          </Typography>
                        </Stack>
                      </Stack>
                    </CardContent>

                    <Divider />

                    <CardActions sx={{ justifyContent: 'space-between', px: 2, py: 1 }}>
                      <Chip
                        label={`${item.vacs_count || 0} créneau(x)`}
                        size="small"
                        variant="outlined"
                      />
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title={t('commun.edit')}>
                          <IconButton size="small" color="primary" onClick={() => openEdit(item)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('commun.delete')}>
                          <IconButton size="small" color="error" onClick={() => handleDelete(item)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}

            {plannings.length === 0 && !loading && (
              <Grid item xs={12}>
                <Paper elevation={1} sx={{ p: 5, textAlign: 'center', borderRadius: 3 }}>
                  <CalendarIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1.5 }} />
                  <Typography variant="h6" color="text.secondary">
                    {t('academy.planning_theorique.no_data')}
                  </Typography>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={openNew} sx={{ mt: 2 }}>
                    {t('academy.planning_theorique.new_button')}
                  </Button>
                </Paper>
              </Grid>
            )}
          </Grid>
        ) : (
          <GenericTable
            columns={tableColumns}
            data={plannings}
            loading={loading}
            onEdit={openEdit}
            onDelete={handleDelete}
            searchPlaceholder={t('academy.planning_theorique.search_placeholder')}
          />
        )}
      </Box>
    </GenericPage>
  );
};

export default TheoreticalPlanningManagePage;
